<template>
  <!--
    Kontener z dynamicznymi klasami (container lub container2 oraz fieldName).
    Obsługuje kliknięcia, ale tylko jeśli nie jest disabled.
  -->
  <span :class="[containerClass, fieldName]" @click="handleClick">
    <!--
      Ukryty checkbox. Kontrolowany przez props:
      - fieldName: nazwa pola
      - fieldValue: wartość
      - checkedCondition: czy zaznaczony (Boolean)
      - disabled: czy zablokowany
      - class: tylko styl, nie wpływa na funkcjonalność
    -->
    <input
      :name="fieldName"
      type="checkbox"
      :value="fieldValue"
      :checked="checkedCondition"
      :disabled="disabled"
      class="checkbox"
    />
    <!--
      "Sztuczny" checkmark czyli własny styl zaznaczenia, zamiast natywnego checkboxa.
      Klasa checkmarkClass decyduje o stylu (np. 'checkmark' lub 'vio')
    -->
    <span :class="checkmarkClass"></span>
  </span>
</template>

<script setup>
// Importujemy funkcje reaktwyne z Vue
import { computed } from 'vue';

// Definiujemy wejściowe propsy
const props = defineProps({
  fieldName: {
    type: [String, Number],
  },
  fieldValue: String,
  checkedCondition: Boolean, // Czy checkbox jest zaznaczony
  disabled: Boolean,         // Czy pole jest zablokowane
  vio: String,               // Jeśli podany, używa klasy 'vio' do stylowania
});

// Definiujemy możliwe eventy, które komponent emituje
const emit = defineEmits(["clicked"]);

// Obsługa kliknięcia w kontenerze checkboxa
const handleClick = () => {
  if (props.disabled) return; // Ignoruj kliknięcia, jeśli disabled
  emit("clicked");
};

// Określenie klasy kontenera: domyślnie 'container', jeśli vio to 'container2'
const containerClass = computed(() => {
  return props.vio ? 'container2' : 'container';
});

// Określenie klasy dla checkmarka: domyślnie 'checkmark', jeśli vio to wartość z vio (np. 'vio')
const checkmarkClass = computed(() => {
  return props.vio ? props.vio : 'checkmark';
});
</script>

<style scoped>
/* Kontener podstawowy – mały kwadrat obok tekstu */
.container {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  position: relative;
  cursor: pointer;
  user-select: none;
  width: var(--choice-size);
  height: var(--choice-size);
  font-size: 0;  /* niepotrzebny tekst */
  margin: 0;
}

/* Kontener alternatywny (gdy vio jest ustawione) */
.container2 {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  position: relative;
  cursor: pointer;
  user-select: none;
  width: var(--choice-size);
  height: var(--choice-size);
  font-size: 0;
  margin: 0;
}

/* Ukrycie natywnego checkboxa */
.container input,
.container2 input {
  position: absolute;
  opacity: 0;
  cursor: pointer;
  height: 0;
  width: 0;
}

/* Wizualny checkmark (kółko) */
.checkmark,
.vio {
  position: absolute;
  inset: 0;            /* top:0; right:0; bottom:0; left:0; */
  border-radius: 50%;
  border: 1px solid var(--border-strong);
  background-color: var(--surface-primary);
  transition: background-color var(--transition-fast), border-color var(--transition-fast);
}

/* Hover na kontenerze: podkreśla obramowanie kontrolki */
.container:hover input ~ .checkmark,
.container2:hover input ~ .vio {
  background-color: var(--surface-selected);
  border-color: var(--text-secondary);
}

/* Jeśli zaznaczony: wypełnij kółko kolorem */
.container input:checked ~ .checkmark,
.container2 input:checked ~ .vio {
  background-color: var(--accent-strong);
  border-color: var(--accent-strong);
}

/* Widoczny focus dla obsługi klawiaturą (input jest wizualnie ukryty). */
.container input:focus-visible ~ .checkmark,
.container2 input:focus-visible ~ .vio {
  outline: 2px solid var(--border-focus);
  outline-offset: 2px;
}

/* Stan zablokowany */
.container input:disabled ~ .checkmark,
.container2 input:disabled ~ .vio {
  background-color: var(--surface-disabled);
  border-color: var(--border-subtle);
  cursor: not-allowed;
}

/* Ukryta pseudo-zaznaczenie (ramka) */
.checkmark:after,
.vio:after {
  content: "";
  position: absolute;
  display: none;
}

/* Wyświetl ramkę, jeśli checkbox jest zaznaczony */
.container input:checked ~ .checkmark:after,
.container2 input:checked ~ .vio:after {
  display: block;
}

/* Wewnętrzny pierścień daje „wypełnioną kropkę w obwódce" — kontrolka czytelna
   także dla osób nierozróżniających kolorów (różnica w kształcie, nie tylko barwie). */
.container .checkmark:after,
.container2 .vio:after {
  inset: 0;
  border: 2px solid var(--surface-primary);
  border-radius: 50%;
}
</style>
